import React, { useEffect, useState, useMemo } from 'react';
import { AlertTriangle, TrendingUp, Loader2, ChevronDown, ChevronUp, Users, DollarSign, MapPin, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_CONFIG = {
  'Severe': { color: 'bg-red-100 text-red-700 border-red-200', bar: '#dc2626', dot: 'bg-red-500' },
  'High': { color: 'bg-orange-100 text-orange-700 border-orange-200', bar: '#ea580c', dot: 'bg-orange-500' },
  'Moderate': { color: 'bg-amber-100 text-amber-700 border-amber-200', bar: '#d97706', dot: 'bg-amber-500' },
  'Low': { color: 'bg-gray-100 text-gray-600 border-gray-200', bar: '#6b7280', dot: 'bg-gray-400' },
};

const SUPPLY_COLORS = {
  'Very Low': 'text-red-600 font-bold',
  'Low': 'text-orange-600 font-semibold',
  'Moderate': 'text-amber-600',
  'Adequate': 'text-green-600',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0A1F44] text-white px-3 py-2 rounded-lg text-xs shadow-xl">
      <p className="font-medium">{label}</p>
      <p>Shortage Index: {payload[0].value}</p>
    </div>
  );
};

const TalentShortageIndex = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('shortage_index');
  const [sortDir, setSortDir] = useState('desc');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/intelligence/talent-shortage?limit=50`)
      .then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(f); setSortDir('desc'); }
  };

  const roles = data?.roles || [];
  const filtered = useMemo(() => {
    let arr = severityFilter === 'all' ? roles : roles.filter(r => r.severity === severityFilter);
    return [...arr].sort((a, b) => {
      const m = sortDir === 'desc' ? -1 : 1;
      if (sortField === 'role') return m * a.role.localeCompare(b.role);
      return m * ((a[sortField] || 0) - (b[sortField] || 0));
    });
  }, [roles, sortField, sortDir, severityFilter]);

  const dist = data?.severity_distribution || {};
  const chartData = roles.slice(0, 20).map(r => ({
    role: r.role.length > 18 ? r.role.slice(0, 16) + '..' : r.role,
    index: r.shortage_index,
    severity: r.severity,
  }));

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />;
  };

  return (
    <IntelligenceLayout title="Talent Shortage Index" embedded={embedded}>
      <div className="p-6 space-y-6" data-testid="talent-shortage-page">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Talent Shortage Index</h1>
          <p className="text-sm text-gray-500 mt-1">Roles with high demand vs. constrained talent supply — focus your pipeline on hard-to-fill positions</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricCard label="Roles Analyzed" value={data?.total_analyzed || 0} icon={BarChart3} />
              <MetricCard label="Severe" value={dist.Severe || 0} subtext="Critical shortage" icon={AlertTriangle} />
              <MetricCard label="High" value={dist.High || 0} subtext="Significant gap" icon={TrendingUp} />
              <MetricCard label="Moderate" value={dist.Moderate || 0} icon={Users} />
              <MetricCard label="Low" value={dist.Low || 0} icon={Users} />
            </div>

            {/* Severity Filter Buttons */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="severity-filters">
              {['all', 'Severe', 'High', 'Moderate', 'Low'].map(s => (
                <button key={s} onClick={() => setSeverityFilter(s)} data-testid={`filter-${s.toLowerCase()}`}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all ${
                    severityFilter === s
                      ? 'bg-[#0A1F44] text-white border-[#0A1F44]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#D4AF37]'
                  }`}>
                  {s === 'all' ? 'All Severities' : s} {s !== 'all' && `(${dist[s] || 0})`}
                </button>
              ))}
            </div>

            {/* Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">Top 20 Shortage Roles</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 120, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="role" type="category" tick={{ fontSize: 11 }} width={115} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="index" radius={[0, 4, 4, 0]} barSize={16}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={SEVERITY_CONFIG[entry.severity]?.bar || '#6b7280'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="shortage-table">
                  <thead className="bg-[#0A1F44] text-white">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs cursor-pointer" onClick={() => handleSort('role')}>
                        <div className="flex items-center gap-1">Role <SortIcon field="role" /></div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs cursor-pointer" onClick={() => handleSort('shortage_index')}>
                        <div className="flex items-center justify-center gap-1">Index <SortIcon field="shortage_index" /></div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs">Severity</th>
                      <th className="text-center px-3 py-3 text-xs cursor-pointer" onClick={() => handleSort('demand_count')}>
                        <div className="flex items-center justify-center gap-1">Demand <SortIcon field="demand_count" /></div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs">Demand Level</th>
                      <th className="text-center px-3 py-3 text-xs">Supply</th>
                      <th className="text-center px-3 py-3 text-xs cursor-pointer" onClick={() => handleSort('companies_hiring')}>
                        <div className="flex items-center justify-center gap-1">Companies <SortIcon field="companies_hiring" /></div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs cursor-pointer" onClick={() => handleSort('state_spread')}>
                        <div className="flex items-center justify-center gap-1">States <SortIcon field="state_spread" /></div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs">Avg Salary</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((r, i) => {
                      const sev = SEVERITY_CONFIG[r.severity] || SEVERITY_CONFIG.Low;
                      return (
                        <tr key={i} className="hover:bg-[#D4AF37]/5 transition-colors" data-testid={`shortage-row-${i}`}>
                          <td className="px-4 py-3 font-medium text-[#0A1F44]">{r.role}</td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${r.shortage_index}%`, backgroundColor: sev.bar }} />
                              </div>
                              <span className="text-xs font-bold text-[#0A1F44]">{r.shortage_index}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2.5 py-0.5 text-[10px] font-semibold rounded-full border ${sev.color}`}>{r.severity}</span>
                          </td>
                          <td className="px-3 py-3 text-center font-semibold text-[#0A1F44]">{r.demand_count?.toLocaleString()}</td>
                          <td className="px-3 py-3 text-center text-xs">{r.demand_level}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-xs ${SUPPLY_COLORS[r.supply_level] || ''}`}>{r.supply_level}</span>
                          </td>
                          <td className="px-3 py-3 text-center text-gray-600">{r.companies_hiring}</td>
                          <td className="px-3 py-3 text-center text-gray-600">{r.state_spread}</td>
                          <td className="px-3 py-3 text-center text-gray-600">
                            {r.avg_salary ? `$${(r.avg_salary / 1000).toFixed(0)}K` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filtered.length === 0 && <p className="text-center py-12 text-gray-400 text-sm">No roles match selected filter</p>}
            </div>

            {/* Methodology */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-[#0A1F44] mb-3">Scoring Methodology</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-600">
                <div>
                  <p className="font-semibold text-[#0A1F44] mb-1">Demand Level (up to 40pts)</p>
                  <p>Very High (500+ openings): 40pts</p>
                  <p>High (200+): 30pts | Moderate (50+): 20pts</p>
                </div>
                <div>
                  <p className="font-semibold text-[#0A1F44] mb-1">Supply Level (up to 40pts)</p>
                  <p>Very Low: 40pts | Low: 30pts</p>
                  <p>Moderate: 20pts | Adequate: 10pts</p>
                </div>
                <div>
                  <p className="font-semibold text-[#0A1F44] mb-1">Severity Bonus (up to 20pts)</p>
                  <p>Severe: 20pts | High: 15pts</p>
                  <p>Moderate: 10pts | Low: 5pts</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </IntelligenceLayout>
  );
};

export default TalentShortageIndex;
